import { useState, useEffect, useCallback, useMemo } from 'react';

import { Dashboard, DashboardPlaceholder } from '../components';
import { fetchStaticData, fetchDashboardSourcePath } from '../requests/requests';
import { DashboardWebsite, WebsiteStaticData, WebsiteStaticDataHealth } from '../types/types';
import { OverallHealth, StatusType } from '../types/enums';
import Display from '../layout/Display';

const GuestDashboard = () => {
  const [resources, setResources] = useState<DashboardWebsite[]>();
  const [path, setPath] = useState('');
  const [displayError, setDisplayError] = useState('');

  const healthInfo = useMemo(() => {
    if (!resources) {
      return initialHealthInfo;
    }

    return resources.reduce(
      (acc, site) => {
        acc[site.overallHealth] += 1;
        return acc;
      },
      { ...initialHealthInfo }
    );
  }, [resources]);

  const refreshStaticData = useCallback(() => {
    fetchStaticData(path)
      .then((data) => {
        if (data.monitors && data.monitors.length) {
          setResources(calculateOverallHealths(data.monitors));
        } else {
          setDisplayError(
            'Looks like this dashboard is empty.\r\n It might take us a few minutes to synchronize dashboard settings.'
          );
        }
      })
      .catch(() => {
        setDisplayError('It might take us a few minutes to display your public dashboard.');
      });
  }, [path]);

  useEffect(() => {
    if (path) {
      refreshStaticData();
      const timer = setInterval(refreshStaticData, 5 * 1000);

      return () => clearInterval(timer);
    }
  }, [path]);

  useEffect(() => {
    fetchDashboardSourcePath(
      window.location.href.split(process.env.REACT_APP_PUBLIC_DASHBOARD_VIEWER_ROOT as string)[1]
    )
      .then(({ data }) => {
        if (data.path) {
          setPath(data.path);
        }
      })
      .catch(() => {
        setDisplayError('We could not find the dashboard you are looking for!');
      });
  }, []);

  if (!resources) {
    return (
      <Display>
        <DashboardPlaceholder title={displayError} />
      </Display>
    );
  }

  return (
    <Display healthInfo={healthInfo}>
      <Dashboard resources={resources} />
    </Display>
  );
};

const initialHealthInfo = {
  [OverallHealth.ERROR]: 0,
  [OverallHealth.WARNING]: 0,
  [OverallHealth.UNAVAILABLE]: 0,
  [OverallHealth.OK]: 0,
  [OverallHealth.UNKNOWN]: 0
};

const calculateOverallHealths = (staticData: WebsiteStaticData[]): DashboardWebsite[] => {
  return staticData.map((site) => ({
    id: site.id,
    url: site.data.url,
    name: site.displayName,
    iconPath: site.data.icon_src,
    dashboards: [],
    health: site.health,
    overallHealth: getOverallHealthIndicator(
      [StatusType.UNAVAILABLE, StatusType.ERROR, StatusType.WARNING, StatusType.IN_PROGRESS].find(
        (status) => Object.values(site.health).includes(status)
      )
    ),
    statusText: getStatusText(site.health)
  }));
};

const statusTextByHealthKey = {
  httpStatus: 'STATUS CODE',
  keywords: 'CONTENT',
  loadSpeed: 'SPEED',
  sslStatus: 'SSL CERTIFICATE'
};

const getStatusText = (health: WebsiteStaticDataHealth) => {
  const errors = Object.entries(health).reduce((acc: string[], [key, value]) => {
    if ([StatusType.ERROR, StatusType.WARNING].includes(value)) {
      return [...acc, statusTextByHealthKey[key as keyof WebsiteStaticDataHealth]];
    }

    return acc;
  }, []);

  if (!errors.length) {
    return '';
  }

  const statusText = 'Problems with ' + errors.join(', ');
  statusText.replace(/,([^,]*)$/, ' and$1');

  return statusText;
};

const getOverallHealthIndicator = (status?: StatusType) => {
  switch (status) {
    case StatusType.UNAVAILABLE:
      return OverallHealth.UNAVAILABLE;
    case StatusType.ERROR:
      return OverallHealth.ERROR;
    case StatusType.WARNING:
      return OverallHealth.WARNING;
    case StatusType.IN_PROGRESS:
      return OverallHealth.UNKNOWN;
    default:
      return OverallHealth.OK;
  }
};

export default GuestDashboard;
